'use client';

import { useState } from 'react';
import { ThumbsUp, MessageSquare, Send, X } from 'lucide-react';
import { NewsComment } from '@/types';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { useNewsStore } from '@/store/newsStore';
import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { ActivityLogger } from '@/lib/activityLog';
import { ReportButton } from '@/components/ui/ReportButton';
import { CrisisModal } from '@/components/ui/CrisisModal';
import { InappropriateLanguageModal } from '@/components/ui/InappropriateLanguageModal';
import { InappropriateLanguageBanner } from '@/components/ui/InappropriateLanguageBanner';
import { useContentModeration } from '@/hooks/useContentModeration';

interface NewsCommentSectionProps {
  newsDocumentId: string;
  newsTitle?: string;
  comments: NewsComment[];
}

export function NewsCommentSection({ newsDocumentId, newsTitle, comments }: NewsCommentSectionProps) {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { createComment, updateComment, deleteComment, toggleCommentLike, hasUserLikedComment } = useNewsStore();

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return t.time.justNow;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}${t.time.minutesAgo}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}${t.time.hoursAgo}`;
    return `${Math.floor(diffInSeconds / 86400)}${t.time.daysAgo}`;
  };

  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<{ documentId: string; authorName: string; parentReplyDocId: string } | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);

  // Per-field content moderation (MWBI-873). Each composer gets its own hook
  // instance so an inappropriate word in one field gates only that field's
  // button -- profanity in the reply box must not block an unrelated edit-save.
  // (Only one reply composer and one edit composer can be open at a time, so a
  // single instance per concern is sufficient; we never need one per comment.)
  const newCommentModeration = useContentModeration(newComment);
  const replyModeration = useContentModeration(replyContent);
  const editModeration = useContentModeration(editingContent);

  // Crisis stays informational and never blocks. A single advisory modal is
  // shown whenever any composer matches a crisis keyword; dismissing it clears
  // every instance so it does not immediately re-open from another field.
  const showCrisisModal =
    newCommentModeration.showCrisisModal ||
    replyModeration.showCrisisModal ||
    editModeration.showCrisisModal;
  const dismissCrisisModal = () => {
    newCommentModeration.dismissCrisisModal();
    replyModeration.dismissCrisisModal();
    editModeration.dismissCrisisModal();
  };

  const handleSubmitComment = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    if (!newComment.trim() || sending) return;
    if (newCommentModeration.hasInappropriate) return;
    setSending(true);

    const success = await createComment({
      newsDocumentId,
      content: newComment.trim(),
    });

    if (success) {
      setNewComment('');
      if (newsTitle) {
        ActivityLogger.commentPost(currentUser, newsTitle, newsDocumentId);
      }
    }
    setSending(false);
  };

  const handleSubmitReply = async () => {
    if (!currentUser || !replyContent.trim() || !replyingTo || sending) return;
    if (replyModeration.hasInappropriate) return;
    setSending(true);

    const success = await createComment({
      newsDocumentId,
      content: replyContent.trim(),
      parentCommentDocumentId: replyingTo.documentId,
    });

    if (success) {
      setReplyContent('');
      setReplyingTo(null);
    }
    setSending(false);
  };

  const handleLike = async (commentDocumentId: string) => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    await toggleCommentLike(newsDocumentId, commentDocumentId);
  };

  const handleStartEdit = (comment: NewsComment) => {
    setEditingCommentId(comment.documentId);
    setEditingContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async () => {
    if (!editingCommentId || !editingContent.trim() || editingLoading) return;
    if (editModeration.hasInappropriate) return;
    setEditingLoading(true);
    const success = await updateComment(newsDocumentId, editingCommentId, editingContent.trim());
    setEditingLoading(false);
    if (success) {
      handleCancelEdit();
    }
  };

  const handleDelete = async (commentDocumentId: string) => {
    if (!window.confirm(`${t.common.delete}?`)) return;
    const success = await deleteComment(newsDocumentId, commentDocumentId);
    if (success && editingCommentId === commentDocumentId) {
      handleCancelEdit();
    }
  };

  // Organize comments into parent and child replies
  const topLevelComments = comments.filter(c => !c.parentComment);
  const getChildComments = (parentDocId: string) =>
    comments.filter(c => c.parentComment?.documentId === parentDocId);

  const renderCommentItem = (comment: NewsComment) => {
    const commentAvatarUrl = comment.author?.avatarUrl || null;
    const isCommentLiked = currentUser ? hasUserLikedComment(newsDocumentId, comment.documentId, currentUser.id) : false;
    const childComments = getChildComments(comment.documentId);

    return (
      <div key={comment.documentId} data-testid={`news-comment-${comment.id}`}>
        {/* Parent comment */}
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-background-secondary flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">
            {commentAvatarUrl ? (
              <Image src={commentAvatarUrl} alt={comment.author?.displayName || 'Unknown'} width={32} height={32} className="w-full h-full object-contain" unoptimized />
            ) : (
              <span className="text-foreground-muted">{comment.author?.initials || '??'}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-foreground">{comment.author?.displayName || 'Unknown'}</span>
              <span className="text-xs text-foreground-muted">{getTimeAgo(comment.createdAt)}</span>
            </div>
            {editingCommentId === comment.documentId ? (
              <div className="mt-1 space-y-2">
                <textarea
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value.slice(0, 500))}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary resize-none"
                  data-testid="news-comment-edit-textarea"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={!editingContent.trim() || editingLoading || editModeration.hasInappropriate}
                    className="px-2 py-1 text-xs font-medium text-white bg-foreground rounded hover:opacity-90 disabled:opacity-50"
                    data-testid="news-comment-save-edit-btn"
                  >
                    {editingLoading ? t.common.saving : t.common.save}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={editingLoading}
                    className="px-2 py-1 text-xs font-medium text-foreground-muted hover:text-foreground"
                    data-testid="news-comment-cancel-edit-btn"
                  >
                    {t.common.cancel}
                  </button>
                </div>
                {editModeration.hasInappropriate && (
                  <InappropriateLanguageBanner testIdPrefix="news-comment-edit-inappropriate" />
                )}
              </div>
            ) : (
              <p className="text-sm text-foreground bg-background-secondary rounded-lg px-3 py-2 mt-1 inline-block max-w-full whitespace-pre-wrap" data-testid={`news-comment-body-${comment.documentId}`}>{comment.content}</p>
            )}
            <div className="flex items-center gap-3 mt-1">
              <button
                onClick={() => handleLike(comment.documentId)}
                className={`flex items-center gap-1 text-xs transition-colors ${isCommentLiked ? 'text-primary' : 'text-foreground-muted hover:text-primary'}`}
                data-testid="news-comment-like-btn"
              >
                <ThumbsUp size={12} className={isCommentLiked ? 'fill-current' : ''} />
                <span>{comment.likes || 0}</span>
              </button>
              <button
                onClick={() => {
                  if (!currentUser) {
                    openAuthModal();
                    return;
                  }
                  setReplyingTo({ documentId: comment.documentId, authorName: comment.author?.displayName || 'Unknown', parentReplyDocId: comment.documentId });
                }}
                className="flex items-center gap-1 text-xs text-foreground-muted hover:text-primary"
                data-testid="news-comment-reply-btn"
              >
                <MessageSquare size={12} />
                <span>{t.common.reply}</span>
              </button>
              {currentUser && currentUser.id !== comment.author?.id && (
                <ReportButton contentType="post_comment" contentId={comment.documentId} contentTitle={comment.content.substring(0, 50)} />
              )}
              {currentUser && currentUser.id === comment.author?.id && editingCommentId !== comment.documentId && (
                <>
                  <button onClick={() => handleStartEdit(comment)} className="text-xs text-foreground-muted hover:text-primary" data-testid="news-comment-edit-btn">
                    {t.common.edit}
                  </button>
                  <button onClick={() => handleDelete(comment.documentId)} className="text-xs text-red-600 hover:text-red-700" data-testid="news-comment-delete-btn">
                    {t.common.delete}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Child replies */}
        {childComments.length > 0 && (
          <div className="ml-11 mt-2 space-y-2 border-l-2 border-border pl-4">
            {childComments.map((child) => {
              const childAvatarUrl = child.author?.avatarUrl || null;
              const isChildLiked = currentUser ? hasUserLikedComment(newsDocumentId, child.documentId, currentUser.id) : false;

              return (
                <div key={child.documentId} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-background-secondary flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">
                    {childAvatarUrl ? (
                      <Image src={childAvatarUrl} alt={child.author?.displayName || 'Unknown'} width={24} height={24} className="w-full h-full object-contain" unoptimized />
                    ) : (
                      <span className="text-foreground-muted text-[10px]">{child.author?.initials || '??'}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-xs text-foreground">{child.author?.displayName || 'Unknown'}</span>
                      <span className="text-[10px] text-foreground-muted">{getTimeAgo(child.createdAt)}</span>
                    </div>
                    {editingCommentId === child.documentId ? (
                      <div className="mt-1 space-y-2">
                        <textarea
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value.slice(0, 500))}
                          rows={3}
                          className="w-full px-2 py-1.5 border border-border rounded-lg bg-background text-xs focus:outline-none focus:border-primary resize-none"
                          data-testid="news-comment-edit-textarea"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={!editingContent.trim() || editingLoading || editModeration.hasInappropriate}
                            className="px-2 py-1 text-[10px] font-medium text-white bg-foreground rounded hover:opacity-90 disabled:opacity-50"
                          >
                            {editingLoading ? t.common.saving : t.common.save}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={editingLoading}
                            className="px-2 py-1 text-[10px] font-medium text-foreground-muted hover:text-foreground"
                          >
                            {t.common.cancel}
                          </button>
                        </div>
                        {editModeration.hasInappropriate && (
                          <InappropriateLanguageBanner testIdPrefix="news-comment-edit-inappropriate" />
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-foreground bg-background-secondary rounded-lg px-2 py-1.5 mt-1 inline-block max-w-full whitespace-pre-wrap" data-testid={`news-comment-reply-body-${child.documentId}`}>{child.content}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={() => handleLike(child.documentId)}
                        className={`flex items-center gap-1 text-[10px] transition-colors ${isChildLiked ? 'text-primary' : 'text-foreground-muted hover:text-primary'}`}
                      >
                        <ThumbsUp size={10} className={isChildLiked ? 'fill-current' : ''} />
                        <span>{child.likes || 0}</span>
                      </button>
                      {currentUser && currentUser.id === child.author?.id && editingCommentId !== child.documentId && (
                        <>
                          <button onClick={() => handleStartEdit(child)} className="text-[10px] text-foreground-muted hover:text-primary">
                            {t.common.edit}
                          </button>
                          <button onClick={() => handleDelete(child.documentId)} className="text-[10px] text-red-600 hover:text-red-700">
                            {t.common.delete}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {replyingTo?.parentReplyDocId === comment.documentId && (
          <div className="ml-11 mt-2 border-l-2 border-primary pl-4">
            <div className="flex items-center gap-2 mb-2 text-xs text-primary">
              <span>{t.comments.replyingTo} @{replyingTo.authorName}</span>
              <button onClick={() => { setReplyingTo(null); setReplyContent(''); }} className="text-foreground-muted hover:text-foreground">
                <X size={14} />
              </button>
            </div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden text-xs font-semibold bg-background-secondary">
                {currentUser?.avatarUrl ? (
                  <Image src={currentUser.avatarUrl} alt={currentUser.displayName} width={24} height={24} className="w-full h-full object-contain" unoptimized />
                ) : currentUser ? (
                  <span className="text-white text-[10px]" style={{ backgroundColor: currentUser.avatarColor }}>{currentUser.initials}</span>
                ) : null}
              </div>
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value.slice(0, 500))}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSubmitReply())}
                    placeholder={`${t.comments.replyTo} ${replyingTo.authorName}...`}
                    disabled={sending}
                    className="w-full h-[48px] px-3 py-2 pr-8 border border-border rounded-lg bg-background text-xs focus:outline-none focus:border-primary disabled:opacity-50"
                    autoFocus
                    data-testid="news-comment-reply-input"
                  />
                  <button
                    onClick={handleSubmitReply}
                    disabled={!replyContent.trim() || sending || replyModeration.hasInappropriate}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="news-comment-reply-submit-btn"
                  >
                    <Send size={14} />
                  </button>
                </div>
                {replyModeration.hasInappropriate && (
                  <div className="mt-1">
                    <InappropriateLanguageBanner testIdPrefix="news-comment-reply-inappropriate" />
                  </div>
                )}
                <div className="flex justify-end mt-1">
                  <span className={`text-[10px] ${replyContent.length >= 450 ? 'text-warning' : 'text-foreground-muted'}`}>
                    {replyContent.length}/500
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-4" data-testid="news-comment-section-root">
      <CrisisModal
        open={showCrisisModal}
        onClose={dismissCrisisModal}
        testIdPrefix="news-comment-crisis"
      />
      <InappropriateLanguageModal
        open={newCommentModeration.showInappropriateModal}
        onClose={newCommentModeration.dismissInappropriateModal}
        testIdPrefix="news-comment-new-inappropriate"
      />
      <InappropriateLanguageModal
        open={replyModeration.showInappropriateModal}
        onClose={replyModeration.dismissInappropriateModal}
        testIdPrefix="news-comment-reply-inappropriate"
      />
      <InappropriateLanguageModal
        open={editModeration.showInappropriateModal}
        onClose={editModeration.dismissInappropriateModal}
        testIdPrefix="news-comment-edit-inappropriate"
      />
      {/* Comment input - only show when not replying to a specific comment */}
      {!replyingTo && (
        <div className="flex gap-3 mb-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden text-xs font-semibold bg-background-secondary">
            {currentUser?.avatarUrl ? (
              <Image src={currentUser.avatarUrl} alt={currentUser.displayName} width={32} height={32} className="w-full h-full object-contain" unoptimized />
            ) : currentUser ? (
              <span className="text-white" style={{ backgroundColor: currentUser.avatarColor }}>{currentUser.initials}</span>
            ) : (
              <span className="text-foreground-muted">?</span>
            )}
          </div>
          <div className="flex-1">
            <div className="relative">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value.slice(0, 500))}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmitComment())}
                placeholder={t.comments.shareThoughts}
                disabled={sending}
                rows={2}
                className="w-full px-4 py-2 pr-10 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                data-testid="news-comment-composer-textarea"
              />
              <button
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || sending || newCommentModeration.hasInappropriate}
                className="absolute right-3 bottom-3 text-foreground-muted hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="news-comment-composer-submit"
              >
                <Send size={16} />
              </button>
            </div>
            {newCommentModeration.hasInappropriate && (
              <div className="mt-1">
                <InappropriateLanguageBanner testIdPrefix="news-comment-new-inappropriate" />
              </div>
            )}
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${newComment.length >= 450 ? 'text-warning' : 'text-foreground-muted'}`} data-testid="news-comment-composer-counter">
                {newComment.length}/500
              </span>
            </div>
          </div>
        </div>
      )}

      {topLevelComments.length === 0 ? (
        <p className="text-sm text-foreground-muted text-center py-2" data-testid="news-comment-empty-state">{t.comments.noComments}</p>
      ) : (
        <div className="space-y-3">
          {topLevelComments.map((comment) => (
            renderCommentItem(comment)
          ))}
        </div>
      )}
    </div>
  );
}
